import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ShieldCheck, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUS_META = {
  not_verified: { color: "text-muted-foreground", icon: ShieldCheck, label: "Not verified" },
  pending: { color: "text-yellow-400", icon: Clock, label: "Pending review" },
  verified: { color: "text-[hsl(var(--support))]", icon: CheckCircle2, label: "Verified" },
  rejected: { color: "text-[hsl(var(--oppose))]", icon: XCircle, label: "Rejected" },
};

export default function Verify() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ aadhaar_number: "", full_name: user?.full_name || "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/verification/status").then((r) => setStatus(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const digits = form.aadhaar_number.replace(/\s/g, "");
    if (digits.length !== 12) { toast.error("Aadhaar must be 12 digits"); return; }
    setSubmitting(true);
    try {
      await api.post("/verification/submit", { aadhaar_number: digits, full_name: form.full_name });
      toast.success("Verified! You can now post.");
      await refresh();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  const current = status?.status || "not_verified";
  const Meta = STATUS_META[current];

  return (
    <div className="max-w-2xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-1">Identity verification</div>
        <h1 className="font-serif text-4xl">DigiLocker · Aadhaar</h1>
      </div>

      <div className={`border p-6 mb-8 ${current === "verified" ? "border-[hsl(var(--support))]" : current === "rejected" ? "border-[hsl(var(--oppose))]" : "border-border"}`} data-testid="verify-status-card">
        <div className={`flex items-center gap-3 mb-2 ${Meta.color}`}>
          <Meta.icon className="w-5 h-5" />
          <div className="text-xs font-semibold tracking-wider">{Meta.label}</div>
        </div>
        {status?.submission && (
          <div className="text-sm text-muted-foreground">
            Submitted: {status.submission.aadhaar_number_masked} · {new Date(status.submission.submitted_at).toLocaleString()}
          </div>
        )}
      </div>

      {current === "verified" ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-6">
            You are a verified voice on TRUEBATE. You can create debates, thoughts, and lead groups.
          </p>
          <Button onClick={() => navigate("/create")} className="rounded-xl text-xs font-semibold tracking-wider bg-[hsl(var(--primary))] text-white" data-testid="verify-create-cta">
            Start creating
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="border border-border bg-card p-5">
            <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-2">Mocked verification (auto-approved)</div>
            <p className="text-sm text-muted-foreground">
              Enter any 12-digit number to simulate an Aadhaar submission. You will be verified instantly and can start posting.
            </p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-muted-foreground tracking-wider">Full name (as per Aadhaar)</Label>
            <Input
              className="rounded-xl mt-2 h-11 bg-background border-border"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="verify-name-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-muted-foreground tracking-wider">Aadhaar number</Label>
            <Input
              className="rounded-xl mt-2 h-11 bg-background border-border tracking-widest font-mono"
              placeholder="XXXX XXXX XXXX"
              maxLength={14}
              value={form.aadhaar_number}
              onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value.replace(/[^0-9\s]/g, "") })}
              data-testid="verify-aadhaar-input"
            />
          </div>
          <Button
            onClick={submit}
            disabled={submitting || current === "pending"}
            className="w-full rounded-xl h-11 text-xs font-semibold tracking-wider bg-[hsl(var(--primary))] text-white"
            data-testid="verify-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : current === "pending" ? "Under review" : "Submit for verification"}
          </Button>
        </div>
      )}
    </div>
  );
}
